<template>
    <div class="page contract-signatures-page">
        <div class="page-header">
            <h1>契約簽署紀錄</h1>
            <p class="page-header__desc">
                會員在商城購物或課程報名流程中的同意勾選與契約簽署
            </p>
        </div>

        <div class="content vaporwave-theme">
            <div class="search-filter-section">
                <div class="toolbar">
                    <div class="search-box filters-wrap">
                        <div class="search-row">
                            <a-input
                                v-model:value="filters.keyword"
                                placeholder="會員姓名 / Email / 手機 / 商品名稱"
                                size="middle"
                                class="w-60 search-input-field"
                                allow-clear
                                @press-enter="handleSearch"
                            />
                            <a-select
                                v-model:value="filters.record_type"
                                :options="recordTypeOptions"
                                placeholder="紀錄類型"
                                size="middle"
                                class="status-select w-40"
                            />
                            <a-select
                                v-model:value="filters.status"
                                :options="statusOptions"
                                placeholder="契約狀態"
                                size="middle"
                                class="status-select w-40"
                                allow-clear
                            />
                            <a-range-picker
                                v-model:value="dateRange"
                                :placeholder="['日期起', '日期迄']"
                                size="middle"
                                class="status-select"
                                @change="handleDateRangeChange"
                            />
                        </div>
                    </div>
                    <a-space class="toolbar-actions">
                        <a-button
                            v-if="canDelete && selectedRowKeys.length > 0"
                            size="middle"
                            danger
                            :loading="batchDeleteLoading"
                            @click="openBatchDeleteModal"
                        >
                            批量刪除 ({{ selectedRowKeys.length }})
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <DataTable
                title=""
                :columns="columns"
                :data-source="records"
                :pagination="pagination"
                :loading="loading"
                :no-card="true"
                :row-selection="rowSelection"
                @refresh="loadRecords"
                @change="handleTableChange"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'user'">
                        <a
                            v-if="record.user?.id || record.user_id"
                            href="#"
                            class="cell-link"
                            @click.prevent="
                                goMember(record.user?.id || record.user_id)
                            "
                        >
                            {{
                                record.user?.name ||
                                record.contract_name ||
                                '會員詳情'
                            }}
                        </a>
                        <span v-else>{{ record.contract_name || '-' }}</span>
                    </template>

                    <template v-if="column.key === 'product'">
                        <a
                            v-if="record.product?.id"
                            href="#"
                            class="cell-link"
                            @click.prevent="goProductView(record.product.id)"
                        >
                            {{ record.product?.name || '商品詳情' }}
                        </a>
                        <span v-else>-</span>
                    </template>

                    <template v-if="column.key === 'requirement'">
                        <a-tag
                            :color="
                                record.record_type === 'contract'
                                    ? 'purple'
                                    : 'cyan'
                            "
                        >
                            {{ record.requirement_label || '-' }}
                        </a-tag>
                    </template>

                    <template v-if="column.key === 'consent'">
                        <div class="cell-stack">
                            <span>{{ record.consent_status || '-' }}</span>
                            <span v-if="record.consent_at" class="sub-text">
                                {{ formatDateTime(record.consent_at) }}
                            </span>
                        </div>
                    </template>

                    <template v-if="column.key === 'contract'">
                        <div class="cell-stack">
                            <a-tag
                                v-if="record.contract_status"
                                :color="getStatusColor(record.contract_status)"
                            >
                                {{ record.contract_status_label }}
                            </a-tag>
                            <span v-else>—</span>
                            <span
                                v-if="record.contract_signed_at"
                                class="sub-text"
                            >
                                {{ formatDateTime(record.contract_signed_at) }}
                            </span>
                        </div>
                    </template>

                    <template v-if="column.key === 'actions'">
                        <a-space>
                            <a-button
                                v-if="record.record_type === 'contract'"
                                size="small"
                                @click="handleViewContractDetail(record)"
                            >
                                詳情
                            </a-button>
                            <a-button
                                v-if="
                                    record.can_preview_pdf &&
                                    record.enrollment_flow_id
                                "
                                size="small"
                                type="link"
                                @click="openPreviewPdf(record)"
                            >
                                預覽 PDF
                            </a-button>
                            <a-button
                                v-if="
                                    record.can_signed_pdf &&
                                    record.enrollment_flow_id
                                "
                                size="small"
                                type="link"
                                @click="openSignedPdf(record)"
                            >
                                已簽 PDF
                            </a-button>
                            <a-button
                                v-if="record.order_id"
                                size="small"
                                type="link"
                                @click="goOrder(record.order_id)"
                            >
                                訂單
                            </a-button>
                            <a-button
                                v-if="canDelete && record.can_delete !== false"
                                size="small"
                                type="link"
                                danger
                                :loading="deletingId === record.id"
                                @click="openDeleteModal(record)"
                            >
                                刪除
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <a-modal
            v-model:open="detailVisible"
            title="契約簽署詳情"
            :width="760"
            :footer="null"
        >
            <template v-if="selected">
                <a-descriptions :column="2" bordered size="small">
                    <a-descriptions-item label="紀錄 ID">
                        {{ selected.id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="狀態">
                        <a-tag :color="getStatusColor(selected.status)">
                            {{ selected.status_label }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="會員">
                        {{ selected.user?.name || '-' }}
                        <span v-if="selected.user?.email" class="sub-text">
                            （{{ selected.user.email }}）
                        </span>
                    </a-descriptions-item>
                    <a-descriptions-item label="手機">
                        {{
                            selected.user?.phone ||
                            selected.contract_phone ||
                            '-'
                        }}
                    </a-descriptions-item>
                    <a-descriptions-item label="商品" :span="2">
                        {{ selected.product?.name || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="簽署人姓名">
                        {{ selected.contract_name || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="身分證字號">
                        {{ selected.contract_id_number || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="同意勾選時間">
                        {{ formatDateTime(selected.agreements_accepted_at) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="契約簽署時間">
                        {{ formatDateTime(selected.signed_at) }}
                    </a-descriptions-item>
                    <a-descriptions-item
                        v-if="selected.order_no"
                        label="關聯訂單"
                        :span="2"
                    >
                        {{ selected.order_no }}
                    </a-descriptions-item>
                </a-descriptions>

                <div v-if="agreementItems.length" class="agreement-list">
                    <div class="section-label">同意項目</div>
                    <ul>
                        <li v-for="item in agreementItems" :key="item.key">
                            {{ item.label }}
                            <span class="sub-text">
                                （{{ formatDateTime(item.accepted_at) }}）
                            </span>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="
                        signatureImageUrl ||
                        signatureImageLoading ||
                        signatureImageError
                    "
                    class="signature-preview"
                >
                    <div class="section-label">簽名圖</div>
                    <div
                        v-if="signatureImageLoading"
                        class="signature-preview__loading"
                    >
                        載入中…
                    </div>
                    <img
                        v-else-if="signatureImageUrl"
                        :src="signatureImageUrl"
                        alt="簽名"
                    />
                    <p v-else class="sub-text">
                        {{ signatureImageError || '無法顯示簽名圖' }}
                    </p>
                </div>

                <div class="detail-actions">
                    <a-button
                        v-if="selected.status === 'draft'"
                        @click="openPreviewPdfFromDetail"
                    >
                        預覽 PDF
                    </a-button>
                    <a-button
                        v-if="canPreviewSignedPdf(selected)"
                        type="primary"
                        @click="openSignedPdfFromDetail"
                    >
                        查看已簽署 PDF
                    </a-button>
                </div>
            </template>
        </a-modal>

        <a-modal
            v-model:open="deleteModalVisible"
            title="刪除簽署紀錄"
            :width="600"
            :confirm-loading="deleteLoading"
            @cancel="deleteModalVisible = false"
        >
            <div v-if="deleteTarget" class="delete-modal-content">
                <div class="delete-record-info">
                    <h3 class="delete-section-title">紀錄資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="會員">
                            {{
                                deleteTarget.user?.name ||
                                deleteTarget.contract_name ||
                                '-'
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item label="商品">
                            {{ deleteTarget.product?.name || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="購買要求">
                            <a-tag
                                :color="
                                    deleteTarget.record_type === 'contract'
                                        ? 'purple'
                                        : 'cyan'
                                "
                            >
                                {{ deleteTarget.requirement_label || '-' }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="同意狀態">
                            {{ deleteTarget.consent_status || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteTarget.contract_status_label"
                            label="契約狀態"
                            :span="2"
                        >
                            {{ deleteTarget.contract_status_label }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
                <div class="delete-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="deleteModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="deleteLoading"
                    @click="confirmDelete"
                >
                    確認刪除
                </a-button>
            </template>
        </a-modal>

        <a-modal
            v-model:open="batchDeleteModalVisible"
            title="批量刪除簽署紀錄"
            :width="600"
            :confirm-loading="batchDeleteLoading"
            @cancel="batchDeleteModalVisible = false"
        >
            <div class="delete-modal-content">
                <p class="batch-delete-summary">
                    已選取
                    <strong>{{ selectedRowKeys.length }}</strong>
                    筆紀錄，確定要全部刪除嗎？
                </p>
                <ul
                    v-if="selectedRecordsPreview.length"
                    class="batch-delete-list"
                >
                    <li
                        v-for="record in selectedRecordsPreview"
                        :key="record.id"
                    >
                        {{ record.user?.name || record.contract_name || '-' }}
                        ／
                        {{ record.product?.name || '-' }}
                        （{{ record.requirement_label || '-' }}）
                    </li>
                </ul>
                <p
                    v-if="
                        selectedRowKeys.length > selectedRecordsPreview.length
                    "
                    class="sub-text"
                >
                    另有
                    {{ selectedRowKeys.length - selectedRecordsPreview.length }}
                    筆未顯示…
                </p>
                <div class="delete-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="batchDeleteModalVisible = false">
                    取消
                </a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="batchDeleteLoading"
                    @click="confirmBatchDelete"
                >
                    確認刪除
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
import {
    computed,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch
} from 'vue';
import dayjs from 'dayjs';
import { message } from 'ant-design-vue';
import DataTable from '@/components/common/DataTable.vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    canPreviewSignedPdf,
    fetchEnrollmentSignatureImageBlob,
    getEnrollmentFlow,
    openEnrollmentFlowPreviewPdf,
    openEnrollmentFlowSignedPdf,
    resolveEnrollmentFlowId,
    resolveEnrollmentSignatureImageUrl
} from '@/api/products/enrollmentFlows';
import {
    deletePurchaseSigningRecord,
    getPurchaseSigningRecords
} from '@/api/products/purchaseSigningRecords';
import { showErrorMessage } from '@/utils/messageHelper';

const adminStore = useAdminInfo();
const canDelete = computed(() => adminStore.hasPermissionCode('150203'));

const goProductView = (productId) => {
    window.open(
        `/products/products-list?viewId=${productId}`,
        '_blank',
        'noopener,noreferrer'
    );
};
const goMember = (userId) => {
    window.open(`/members/${userId}`, '_blank', 'noopener,noreferrer');
};
const goOrder = (orderId) => {
    window.open(
        `/products/finance-orders?keyword=${encodeURIComponent(String(orderId))}`,
        '_blank',
        'noopener,noreferrer'
    );
};

const loading = ref(false);
const deletingId = ref(null);
const deleteLoading = ref(false);
const batchDeleteLoading = ref(false);
const deleteModalVisible = ref(false);
const batchDeleteModalVisible = ref(false);
const deleteTarget = ref(null);
const selectedRowKeys = ref([]);
const records = ref([]);
const detailVisible = ref(false);
const selected = ref(null);
const dateRange = ref(null);
const signatureImageUrl = ref('');
const signatureImageLoading = ref(false);
const signatureImageError = ref('');
let signatureBlobObjectUrl = '';

const revokeSignatureBlob = () => {
    if (signatureBlobObjectUrl) {
        URL.revokeObjectURL(signatureBlobObjectUrl);
        signatureBlobObjectUrl = '';
    }
    signatureImageUrl.value = '';
    signatureImageError.value = '';
};

const loadSignaturePreview = async (record) => {
    revokeSignatureBlob();
    if (!record) {
        return;
    }
    const flowId = resolveEnrollmentFlowId(record);
    const hasSignature = Boolean(
        record.signature_path || record.signature_image_url
    );
    if (!flowId || !hasSignature) {
        return;
    }

    signatureImageLoading.value = true;
    try {
        const blob = await fetchEnrollmentSignatureImageBlob(flowId);
        if (!(blob instanceof Blob) || blob.size === 0) {
            throw new Error('簽名圖為空');
        }
        // API 錯誤時可能回 JSON blob
        if (blob.type && /json|text/i.test(blob.type)) {
            let msg = '簽名圖不存在';
            try {
                const text = await blob.text();
                const json = JSON.parse(text);
                msg = json.message || msg;
            } catch {
                // ignore
            }
            throw new Error(msg);
        }
        signatureBlobObjectUrl = URL.createObjectURL(blob);
        signatureImageUrl.value = signatureBlobObjectUrl;
    } catch (error) {
        const fallback = resolveEnrollmentSignatureImageUrl(record);
        if (fallback) {
            signatureImageUrl.value = fallback;
        } else {
            signatureImageError.value = error?.message || '無法載入簽名圖';
        }
    } finally {
        signatureImageLoading.value = false;
    }
};

const agreementItems = computed(() => {
    const items = selected.value?.agreements_accepted;
    if (!items || typeof items !== 'object') {
        return [];
    }
    return Object.entries(items).map(([key, value]) => ({
        key,
        label: value?.label || key,
        accepted_at: value?.accepted_at
    }));
});

const filters = reactive({
    keyword: '',
    record_type: 'all',
    status: undefined,
    signed_from: undefined,
    signed_to: undefined
});

const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0
});

const recordTypeOptions = [
    { label: '全部', value: 'all' },
    { label: '契約簽署', value: 'contract' },
    { label: '同意條款', value: 'consent' }
];

const statusOptions = [
    { label: '填寫中', value: 'draft' },
    { label: '已簽署', value: 'contract_signed' },
    { label: '待付款', value: 'payment_pending' },
    { label: '已付款', value: 'paid' },
    { label: '報名完成', value: 'enrollment_completed' },
    { label: '已過期', value: 'expired' },
    { label: '已取消', value: 'cancelled' }
];

const columns = [
    { title: '會員', key: 'user', width: 110 },
    { title: '商品', key: 'product', width: 160 },
    { title: '購買要求', key: 'requirement', width: 100 },
    { title: '同意狀態／時間', key: 'consent', width: 170 },
    { title: '契約狀態／時間', key: 'contract', width: 170 },
    { title: '操作', key: 'actions', width: 240, fixed: 'right' }
];

const rowSelection = computed(() => {
    if (!canDelete.value) {
        return undefined;
    }

    return {
        selectedRowKeys: selectedRowKeys.value,
        onChange: (keys) => {
            selectedRowKeys.value = keys;
        },
        getCheckboxProps: (record) => ({
            disabled: record.can_delete === false
        })
    };
});

const selectedRecordsPreview = computed(() =>
    records.value
        .filter((record) => selectedRowKeys.value.includes(record.id))
        .slice(0, 5)
);

const getStatusColor = (status) => {
    const map = {
        draft: 'default',
        contract_signed: 'blue',
        payment_pending: 'orange',
        paid: 'green',
        enrollment_completed: 'cyan',
        expired: 'red',
        cancelled: 'default'
    };
    return map[status] || 'default';
};

const formatDateTime = (value) => {
    if (!value) return '-';
    return dayjs(value).format('YYYY-MM-DD HH:mm');
};

const loadRecords = async () => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            record_type: filters.record_type
        };
        if (filters.keyword) params.keyword = filters.keyword;
        if (filters.status) params.status = filters.status;
        if (filters.signed_from) params.signed_from = filters.signed_from;
        if (filters.signed_to) params.signed_to = filters.signed_to;

        const response = await getPurchaseSigningRecords(params);
        if (response?.result) {
            records.value = response.data?.items || [];
            pagination.total = response.data?.total || 0;
            pagination.current = response.data?.current_page || 1;
        } else {
            records.value = [];
            pagination.total = 0;
        }
    } catch (error) {
        showErrorMessage('載入簽署紀錄失敗：' + (error.message || '未知錯誤'));
        records.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

const handleDateRangeChange = (dates) => {
    if (dates?.length === 2) {
        filters.signed_from = dayjs(dates[0]).format('YYYY-MM-DD');
        filters.signed_to = dayjs(dates[1]).format('YYYY-MM-DD');
    } else {
        filters.signed_from = undefined;
        filters.signed_to = undefined;
        dateRange.value = null;
    }
};

const handleSearch = () => {
    pagination.current = 1;
    selectedRowKeys.value = [];
    loadRecords();
};

const handleReset = () => {
    filters.keyword = '';
    filters.record_type = 'all';
    filters.status = undefined;
    filters.signed_from = undefined;
    filters.signed_to = undefined;
    dateRange.value = null;
    pagination.current = 1;
    selectedRowKeys.value = [];
    loadRecords();
};

const handleTableChange = ({ pagination: pag }) => {
    if (pag) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        selectedRowKeys.value = [];
        loadRecords();
    }
};

const handleViewContractDetail = async (record) => {
    if (!record.enrollment_flow_id) {
        return;
    }
    try {
        const response = await getEnrollmentFlow(record.enrollment_flow_id);
        if (response?.result) {
            selected.value = response.data;
            detailVisible.value = true;
            await loadSignaturePreview(response.data);
        }
    } catch (error) {
        showErrorMessage('載入詳情失敗：' + (error.message || '未知錯誤'));
    }
};

watch(detailVisible, (open) => {
    if (!open) {
        revokeSignatureBlob();
        selected.value = null;
    }
});

onBeforeUnmount(() => {
    revokeSignatureBlob();
});

const openPreviewPdf = async (record) => {
    const flowId = resolveEnrollmentFlowId(record);
    if (!flowId) {
        showErrorMessage('無法開啟預覽 PDF：缺少契約簽署紀錄 ID');
        return;
    }
    try {
        await openEnrollmentFlowPreviewPdf(flowId);
    } catch (error) {
        showErrorMessage('無法開啟預覽 PDF：' + (error.message || '未知錯誤'));
    }
};

const openSignedPdf = async (record) => {
    const flowId = resolveEnrollmentFlowId(record);
    if (!flowId) {
        showErrorMessage('無法開啟 PDF：缺少契約簽署紀錄 ID');
        return;
    }
    try {
        await openEnrollmentFlowSignedPdf(flowId);
    } catch (error) {
        const raw = String(error?.message || '');
        const friendly =
            /NoSuchKey|No such object|does not exist|找不到契約/i.test(raw)
                ? '找不到契約檔案，可能尚未產生或已失效。'
                : raw || '載入失敗';
        showErrorMessage(friendly);
    }
};

const openPreviewPdfFromDetail = () => openPreviewPdf(selected.value);
const openSignedPdfFromDetail = () => openSignedPdf(selected.value);

const openDeleteModal = (record) => {
    deleteTarget.value = record;
    deleteModalVisible.value = true;
};

const openBatchDeleteModal = () => {
    if (selectedRowKeys.value.length === 0) {
        return;
    }
    batchDeleteModalVisible.value = true;
};

const confirmDelete = async () => {
    if (!deleteTarget.value?.id) {
        return;
    }

    deleteLoading.value = true;
    deletingId.value = deleteTarget.value.id;
    try {
        const response = await deletePurchaseSigningRecord(
            deleteTarget.value.id
        );
        if (response?.result) {
            message.success('刪除成功');
            if (
                detailVisible.value &&
                selected.value?.id === deleteTarget.value.id
            ) {
                detailVisible.value = false;
                selected.value = null;
            }
            selectedRowKeys.value = selectedRowKeys.value.filter(
                (key) => key !== deleteTarget.value.id
            );
            deleteModalVisible.value = false;
            deleteTarget.value = null;
            loadRecords();
        } else {
            showErrorMessage(response?.message || '刪除失敗');
        }
    } catch (error) {
        showErrorMessage(
            error?.response?.data?.message || error?.message || '刪除失敗'
        );
    } finally {
        deleteLoading.value = false;
        deletingId.value = null;
    }
};

const confirmBatchDelete = async () => {
    if (selectedRowKeys.value.length === 0) {
        return;
    }

    batchDeleteLoading.value = true;
    let successCount = 0;
    let failedCount = 0;
    let firstError = '';

    for (const recordKey of [...selectedRowKeys.value]) {
        try {
            const response = await deletePurchaseSigningRecord(recordKey);
            if (response?.result) {
                successCount += 1;
            } else {
                failedCount += 1;
                firstError = firstError || response?.message || '刪除失敗';
            }
        } catch (error) {
            failedCount += 1;
            firstError =
                firstError ||
                error?.response?.data?.message ||
                error?.message ||
                '刪除失敗';
        }
    }

    batchDeleteLoading.value = false;
    batchDeleteModalVisible.value = false;
    selectedRowKeys.value = [];

    if (failedCount === 0) {
        message.success(`成功刪除 ${successCount} 筆紀錄`);
    } else if (successCount > 0) {
        message.warning(`已刪除 ${successCount} 筆，${failedCount} 筆刪除失敗`);
    } else {
        showErrorMessage(firstError || '批量刪除失敗');
    }

    loadRecords();
};

onMounted(() => {
    loadRecords();
});
</script>

<style scoped>
.contract-signatures-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.search-filter-section {
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(200, 200, 210, 0.4);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.search-box {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
}

.search-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
}

.w-60 {
    width: 280px;
    min-width: 200px;
}

.w-40 {
    width: 160px;
    min-width: 140px;
}

.toolbar-actions {
    flex-shrink: 0;
}

.cell-stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.sub-text {
    color: #888;
    font-size: 12px;
}

.cell-link {
    color: #5b4db0;
    text-decoration: none;
    cursor: pointer;
    border-bottom: 1px solid rgba(91, 77, 176, 0.35);
}

.cell-link:hover {
    color: #3f338a;
    border-bottom-color: #3f338a;
}

.agreement-list {
    margin-top: 16px;
}

.agreement-list ul {
    margin: 0;
    padding-left: 1.2em;
}

.section-label {
    margin-bottom: 8px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
}

.signature-preview {
    margin-top: 20px;
}

.signature-preview img {
    max-width: 280px;
    max-height: 100px;
    border: 1px solid rgba(200, 200, 210, 0.5);
    background: #fffef5;
    padding: 8px;
    border-radius: 8px;
}

.signature-preview__loading {
    color: #888;
    font-size: 13px;
}

.detail-actions {
    margin-top: 20px;
    text-align: right;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}

.delete-modal-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.delete-section-title {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 600;
    color: #2a2a3a;
}

.batch-delete-summary {
    margin: 0;
    color: #2a2a3a;
}

.batch-delete-list {
    margin: 0;
    padding-left: 1.2em;
    color: #4a4a5a;
}
</style>
